import React, { useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { allStyles, fieldHeight, textFontSize } from './FormStyles';
import { translate } from './FormUtils';
import TitleLabel from './TitleLabel';

const styles = StyleSheet.create(allStyles);

const MIN_COLUMNS = 3;
const MIN_BUTTON_WIDTH = 100;
const BUTTON_GAP = 8;

// groupIndicator
const SHOW_GROUP_INDICATOR = false;

const TIME_PATTERN = /(\d{1,2}):(\d{2})\s?([AaPp])[Mm]/g;

// reformat every time in the label, e.g. "2:00 PM - 3:00 PM" -> "14:00 - 15:00"
const formatTimeslotLabel = (label, use24HourFormat) => {
    if (!use24HourFormat || typeof label !== 'string') return label;

    return label.replace(TIME_PATTERN, (match, hour, minute, meridiem) => {
        let h = parseInt(hour, 10);

        if (isNaN(h) || h < 1 || h > 12) return match;

        if (meridiem.toLowerCase() === 'p') {
            if (h !== 12) h += 12;
        } else if (h === 12) {
            h = 0;
        }

        return `${h < 10 ? '0' : ''}${h}:${minute}`;
    });
};

export default function FieldTimeslotGroup(props) {
    const { timeslotGroupData, use24HourFormat, value, name, onChange } = props;
    const groups = timeslotGroupData?.group ?? [];

    const [rowWidth, setRowWidth] = useState(0);

    // at least 4 buttons per row, more when the screen is wide enough
    const columns = Math.max(MIN_COLUMNS, Math.floor((rowWidth + BUTTON_GAP) / (MIN_BUTTON_WIDTH + BUTTON_GAP)));
    const buttonWidth = rowWidth > 0 ? (rowWidth - BUTTON_GAP * (columns - 1)) / columns : undefined;

    const onRowLayout = (e) => {
        setRowWidth(e.nativeEvent.layout.width);
    };

    return (
        <View style={[styles.fieldContainer, props.containerStyle]}>
            <TitleLabel {...props} />
            <View onLayout={onRowLayout}>
                {groups.map((group) => {
                    const timeslots = group.timeslot ?? [];
                    const showReason = !group.enable && !!group.message;

                    return (
                        <View key={group.id} style={localStyles.group}>
                            <View style={localStyles.groupHeader}>
                                {SHOW_GROUP_INDICATOR &&
                                    <View style={[localStyles.groupIndicator, { backgroundColor: group.enable ? '#212121' : '#D0D0D0' }]} />
                                }
                                <Text style={[localStyles.groupName, !group.enable && localStyles.disabledText]}>
                                    {group.name}
                                </Text>
                            </View>
                            {showReason &&
                                <Text style={localStyles.reason}>{group.message}</Text>
                            }
                            {(!showReason && timeslots.length === 0) &&
                                <Text style={localStyles.reason}>{translate(['FORM', 'NO_TIMESLOT_IN_GROUP'])}</Text>
                            }
                            {(!!buttonWidth && timeslots.length > 0) &&
                                <View style={localStyles.buttonRow}>
                                    {timeslots.map((slot) => {
                                        const enabled = group.enable && slot.enable;
                                        const selected = enabled && value === slot.value;

                                        return (
                                            <TouchableOpacity
                                                key={slot.value}
                                                disabled={!enabled}
                                                style={[
                                                    localStyles.button,
                                                    { width: buttonWidth },
                                                    selected && localStyles.buttonSelected,
                                                    !enabled && localStyles.buttonDisabled,
                                                ]}
                                                onPress={() => onChange({ name, value: slot.value })}
                                            >
                                                <Text
                                                    numberOfLines={1}
                                                    style={[
                                                        localStyles.buttonText,
                                                        selected && localStyles.buttonTextSelected,
                                                        !enabled && localStyles.disabledText,
                                                    ]}
                                                >
                                                    {formatTimeslotLabel(slot.value, use24HourFormat)}
                                                </Text>
                                            </TouchableOpacity>
                                        );
                                    })}
                                </View>
                            }
                        </View>
                    );
                })}
            </View>
        </View>
    )
}

const localStyles = StyleSheet.create({
    group: {
        marginBottom: 16,
    },
    groupHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 8,
    },
    groupIndicator: {
        width: 4,
        height: 16,
        borderRadius: 2,
        marginRight: 8,
    },
    groupName: {
        color: '#212121',
        fontSize: textFontSize,
        fontWeight: 'bold',
    },
    reason: {
        color: '#9E9E9E',
        fontSize: textFontSize,
        marginBottom: 8,
    },
    buttonRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginRight: -BUTTON_GAP,
    },
    button: {
        minHeight: fieldHeight,
        backgroundColor: '#FFFFFF',
        justifyContent: 'center',
        alignItems: 'center',
        borderColor: '#6E6E6E',
        borderWidth: StyleSheet.hairlineWidth,
        borderRadius: 100,
        paddingHorizontal: 6,
        marginRight: BUTTON_GAP,
        marginBottom: BUTTON_GAP,
    },
    buttonSelected: {
        backgroundColor: '#212121',
        borderColor: '#212121',
    },
    buttonDisabled: {
        backgroundColor: '#F2F2F2',
        borderColor: '#E0E0E0',
    },
    buttonText: {
        color: '#212121',
        fontSize: textFontSize,
        lineHeight: textFontSize * 1.3,
    },
    buttonTextSelected: {
        color: '#FFFFFF',
    },
    disabledText: {
        color: '#B0B0B0',
    },
});
